import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";
import { DashboardLayout } from "./Dashboard";
import { isPermissionNavRole, useAuth } from "@/contexts/use-auth";
import { PatientSummaryMemo } from "@/components/patient-v2/PatientSummary/PatientSummary";
import { PatientTabsMemo } from "@/components/patient-v2/MainContent/PatientTabs";
import { InternalNotesPanelMemo } from "@/components/patient-v2/InternalNotes/InternalNotesPanel";
import { PatientRightPanelFieldsMemo } from "@/components/patient-v2/PatientRightPanel/PatientRightPanelFields";
import { usePatientProfileV2 } from "@/components/patient-v2/usePatientProfileV2";
import { PatientProfilePanelToggle } from "@/components/patient-v2/shared/PatientProfilePanelToggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetData } from "@/hooks/useGetData";
import { formatPlanStatusLabel, getCurrentMonthYear, getStatusBadge } from "@/utils/helpers";
import { cn } from "@/lib/utils";
import {
  resolveReadingCount,
} from "@/components/patient-v2/PatientSummary/readingCountHelpers";
import { PatientV2, type NursingNoteV2 } from "@/components/patient-v2/types";
import { NursingNoteFormDialog } from "@/components/patient-v2/nursingNotes/NursingNoteFormDialog";

function resolveAuthorName(
  profile: { name?: string; firstName?: string; middleName?: string; lastName?: string } | null,
  user: Record<string, unknown> | null,
): string {
  const profileName =
    profile?.name ??
    [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(" ");
  if (profileName && profileName.trim()) return profileName;
  const userRecord = user ?? {};
  return (
    String(
      (userRecord.name as string | undefined) ??
        [userRecord.firstName, userRecord.lastName].filter(Boolean).join(" "),
    ) || "Unknown"
  );
}

function normalizePrograms(programs: unknown): string[] {
  return Array.isArray(programs)
    ? programs.map((program) => String(program).trim().toLowerCase()).filter(Boolean)
    : [];
}

function patientHasCarePlanProgram(programs: string[]) {
  return programs.includes("ccm") || programs.includes("pcm");
}

function patientHasRpmProgram(programs: string[]) {
  return programs.includes("rpm");
}

function shouldShowCarePlanProfileTag(
  programs: string[],
  lastCarePlanStatus?: string,
) {
  if (!patientHasCarePlanProgram(programs)) return false;
  const care = String(lastCarePlanStatus ?? "").trim().toLowerCase();
  return care === "followup" || care === "pending";
}

function shouldShowTreatmentPlanProfileTag(
  programs: string[],
  lastTreatmentPlanStatus?: string,
) {
  if (!patientHasRpmProgram(programs)) return false;
  const treat = String(lastTreatmentPlanStatus ?? "").trim().toLowerCase();
  return treat === "pending";
}

function getDefaultPatientTab(
  tabFromUrl: string | null,
  isCaretaker: boolean,
  caretakerType: string,
) {
  if (tabFromUrl) return tabFromUrl;
  if (isCaretaker && caretakerType === "facility") return "devices";
  if (isCaretaker && caretakerType === "no_facility") return "care-plan";
  return "basic-info";
}

const TABLET_PANEL_QUERY = "(min-width: 700px) and (max-width: 1181px)";

function isTabletPanelRange() {
  return window.matchMedia(TABLET_PANEL_QUERY).matches;
}

export default function PatientProfileV2() {
  console.log("PatientProfileV2 -------------------------------------------------------------------------------");
  const { patientId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, isAuthorized, canAccess } = useAuth();
  const {
    patient : patientData,
    carePlans : carePlansData,
    treatmentPlans : treatmentPlansData,
    devices : devicesData,
    nursingNotes,
    internalNotes,
    loading,
    refetch,
    setInternalNotes,
    setPatient,
  } = usePatientProfileV2(patientId);

  const patient =  useMemo(
    () =>  patientData,
    [patientData]
  );

  const carePlans = useMemo(
    () => carePlansData ?? [],
    [carePlansData]
  );
  
  const treatmentPlans = useMemo(
    () => treatmentPlansData ?? [],
    [treatmentPlansData]
  );
  const  devices = useMemo(() => (devicesData ?? []), [patientId] );

  const userRole = useMemo(() => String((user as { role?: string } | null)?.role ?? ""), [user]);
  const managerScope = useMemo(() => String((user as { managerScope?: string } | null)?.managerScope ?? ""), [user]);
  const isProviderOrPractitioner = useMemo(() => userRole === "provider" || userRole === "practitioner", [userRole]);
  const isProviderOrPractitionerManager =
    useMemo(() => userRole === "manager" && (managerScope === "provider" || managerScope === "practitioner"), [userRole, managerScope]);
  const isCaretaker = useMemo(() => userRole === "caretaker", [userRole]);
  
  const caretakerType = useMemo(
    () => (isCaretaker ? String((profile as { type?: string } | null)?.type ?? "") : ""),
    [isCaretaker, profile],
  );

  const profileId = useMemo(() => String((profile as { _id?: string } | null)?._id ?? ""), [profile]);
  const assignedCaretakerIdList = useMemo(() => (patient?.assignedCaretakerIds ?? []).map((caretaker) =>
    typeof caretaker === "string" ? caretaker : caretaker?._id,
  ), [patient?.assignedCaretakerIds]);
  const isCaretakerAssignedToPatient = useMemo(() =>
    !isCaretaker || (!!profileId && assignedCaretakerIdList.includes(profileId)), [isCaretaker, profileId, assignedCaretakerIdList]);
  const canAssignCaretaker = useMemo(() => {
    return (
    !isProviderOrPractitioner &&
    !isProviderOrPractitionerManager &&
    isCaretakerAssignedToPatient);
  }, [isProviderOrPractitioner, isProviderOrPractitionerManager, isCaretakerAssignedToPatient]);
  const canEditPatient = useMemo(() => {
    return (
    !isProviderOrPractitioner &&
    !isProviderOrPractitionerManager &&
    isCaretakerAssignedToPatient &&
    isAuthorized("canAddPatients"));
  }, [isProviderOrPractitioner, isProviderOrPractitionerManager, isCaretakerAssignedToPatient, isAuthorized]);
  const canUpdatePatientPassword = useMemo(() => {
    return (
    !isProviderOrPractitionerManager &&
    (userRole === "admin" ||
      userRole === "caretaker" ||
      (isPermissionNavRole(userRole) && canAccess("canAccessPatients"))));
  }, [isProviderOrPractitionerManager, userRole, canAccess]);
  const canManageSpecialInstructions = useMemo(() => {
    return (
    (userRole === "admin" || isCaretakerAssignedToPatient) &&
    !isProviderOrPractitioner &&
    !isProviderOrPractitionerManager);
  }, [isProviderOrPractitioner, isProviderOrPractitionerManager, userRole, isCaretakerAssignedToPatient]);

  const authorName = useMemo(
    () =>
      resolveAuthorName(
        (profile as { name?: string; firstName?: string; middleName?: string; lastName?: string } | null) ??
          null,
        (user as Record<string, unknown> | null) ?? null,
      ),
    [profile, user],
  );

  const canSeeInternalNotes = isAuthorized("canAddNursingNotes");
  const canAddNursingNote = useMemo(
    () =>
      !isProviderOrPractitioner &&
      !isProviderOrPractitionerManager &&
      (!isCaretaker || isCaretakerAssignedToPatient) &&
      isAuthorized("canAddNursingNotes") &&
      patient?.status !== "pending",
    [
      isProviderOrPractitioner,
      isProviderOrPractitionerManager,
      isCaretaker,
      isCaretakerAssignedToPatient,
      isAuthorized,
      patient?.status,
    ],
  );

  const [activeTab, setActiveTab] = useState(() =>
    getDefaultPatientTab(
      searchParams.get("tab"),
      isCaretaker,
      String((profile as { type?: string } | null)?.type ?? ""),
    ),
  );
  const hadTabInUrlOnMountRef = useRef(!!searchParams.get("tab"));
  const appliedCaretakerDefaultRef = useRef(
    isCaretaker &&
      (caretakerType === "facility" || caretakerType === "no_facility"),
  );
  const [carePlanSubTab, setCarePlanSubTab] = useState("care-plan");
  const [selectedReadingMonth, setSelectedReadingMonth] = useState(getCurrentMonthYear);
  const [readingCountPending, setReadingCountPending] = useState(false);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
  const [openNursingNoteRequested, setOpenNursingNoteRequested] = useState(false);
  const [nursingNoteDialogMode, setNursingNoteDialogMode] = useState<"add" | "edit" | "view" | null>(
    null,
  );
  const [selectedNursingNote, setSelectedNursingNote] = useState<NursingNoteV2 | null>(null);
  const [nursingNotesRefreshKey, setNursingNotesRefreshKey] = useState(0);

  const {
    apiData: readingCountData,
    loading: readingCountLoading,
    fetchData: fetchReadingCount,
  } = useGetData(
    "/reading/patient-reading-count",
    {
      patientId: patient?._id,
      month: selectedReadingMonth,
    },
    false,
    !!patient?._id,
    () => setReadingCountPending(false),
  );
  const readingCount = useMemo( () =>  resolveReadingCount(readingCountData) , [JSON.stringify(readingCountData)]);

  const refreshReadingCount = useCallback(() => {
    if (!patient?._id) return;
    setReadingCountPending(true);
    fetchReadingCount();
  }, [patient?._id, fetchReadingCount]);

  const onCarePlanClick = useCallback(() => {
    setCarePlanSubTab("care-plan");
    setActiveTab("care-plan");
  }, []);
  const onTxPlanClick = useCallback(() => {
    setCarePlanSubTab("tx-plan");
    setActiveTab("care-plan");
  }, []);

  const openNursingNoteDialog = useCallback(
    (mode: "add" | "edit" | "view", note: NursingNoteV2 | null = null) => {
      setSelectedNursingNote(note);
      setNursingNoteDialogMode(mode);
    },
    [],
  );

  const onOpenNursingNoteFromTab = useCallback(
    (mode: "edit" | "view", note: NursingNoteV2) => {
      openNursingNoteDialog(mode, note);
    },
    [openNursingNoteDialog],
  );

  const onAddNursingNote = useCallback(() => {
    openNursingNoteDialog("add");
  }, [openNursingNoteDialog]);

  const setThePatient = useCallback((nextPatient: PatientV2) => {
    setPatient(nextPatient);
  }, [setPatient]);

  const toggleLeftPanel = useCallback(() => {
    setIsLeftPanelOpen((open) => {
      const next = !open;
      if (next && isTabletPanelRange()) setIsRightPanelOpen(false);
      return next;
    });
  }, []);

  const toggleRightPanel = useCallback(() => {
    setIsRightPanelOpen((open) => {
      const next = !open;
      if (next && isTabletPanelRange()) setIsLeftPanelOpen(false);
      return next;
    });
  }, []);


  useEffect(() => {
    if (!isCaretaker) return;
    if (hadTabInUrlOnMountRef.current) return;
    if (appliedCaretakerDefaultRef.current) return;
    if (caretakerType !== "facility" && caretakerType !== "no_facility") return;

    appliedCaretakerDefaultRef.current = true;
    setActiveTab(caretakerType === "facility" ? "devices" : "care-plan");
  }, [isCaretaker, caretakerType]);

  useEffect(() => {
    if (searchParams.get("openNursingNote") !== "1") return;

    setActiveTab("nursing-notes");
    setOpenNursingNoteRequested(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openNursingNote");
        next.set("tab", "nursing-notes");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!openNursingNoteRequested) return;
    if (loading || !patient) return;
    if (!canAddNursingNote) {
      setOpenNursingNoteRequested(false);
      return;
    }
    openNursingNoteDialog("add");
    setOpenNursingNoteRequested(false);
  }, [openNursingNoteRequested, loading, patient, canAddNursingNote, openNursingNoteDialog]);

  useEffect(() => {
    const media = window.matchMedia(TABLET_PANEL_QUERY);
    const syncTabletPanels = () => {
      if (media.matches && isLeftPanelOpen && isRightPanelOpen) setIsRightPanelOpen(false);
    };

    syncTabletPanels();
    media.addEventListener("change", syncTabletPanels);
    return () => media.removeEventListener("change", syncTabletPanels);
  }, [isLeftPanelOpen, isRightPanelOpen]);
  

  if (loading) {
    return (
      <DashboardLayout className="p-0" activePage="/patients/">
        <div className="flex h-[calc(100vh-4.5rem)] min-h-0 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">Loading patient details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout className="p-0" activePage="/patients/">
        <div className="flex h-[calc(100vh-4.5rem)] min-h-0 items-center justify-center">
          <p className="text-sm text-muted-foreground">Unable to load patient details.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout className="p-0" activePage="/patients/" entityId={patient?.patientId || patientId}
    entityHeaderAfterId={(() => {
      const patientStatus = String(patient?.status ?? "")
        .trim()
        .toLowerCase();

      if (patientStatus === "pending") return;

      const programs = normalizePrograms(patient?.programs);
      const showCare = shouldShowCarePlanProfileTag(
        programs,
        patient?.lastCarePlanStatus,
      );
      const showTreat = shouldShowTreatmentPlanProfileTag(
        programs,
        patient?.lastTreatmentPlanStatus,
      );

      if (!showCare && !showTreat) return null;
      const hasCCMProgram = programs.includes("ccm");
      const hasPCMProgram = programs.includes("pcm");
      const carePlanStatus =
        patient?.lastCarePlanStatus === "followUp"
          ? "Pending Follow Up"
          : patient?.lastCarePlanStatus === "pending"
            ? "Pending"
            : "";
      const careLabel = hasCCMProgram ? "CCM" : hasPCMProgram ? "PCM" : "CCM";
      return (
        <div className="flex items-center gap-2 flex-wrap">
          {showCare ? (
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-medium text-[#8E8E93] tracking-wide">
                {/* {careLabel} */}
                Care Plan
              </span>
              <span
                className="text-[11px] font-medium px-2.5 py-0.5 rounded-full border"
                style={{
                  backgroundColor: getStatusBadge(
                    patient?.lastCarePlanStatus === "followUp"
                      ? "pendingFollowUp"
                      : patient?.lastCarePlanStatus,
                  ).bgColor,
                  color: getStatusBadge(
                    patient?.lastCarePlanStatus === "followUp"
                      ? "pendingFollowUp"
                      : patient?.lastCarePlanStatus,
                  ).mainColor,
                  borderColor: getStatusBadge(
                    patient?.lastCarePlanStatus === "followUp"
                      ? "pendingFollowUp"
                      : patient?.lastCarePlanStatus,
                  ).mainColor,
                }}
              >
                {formatPlanStatusLabel(String(carePlanStatus))}
              </span>
            </div>
          ) : null}
          {showTreat ? (
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-medium text-[#8E8E93] tracking-wide">
                Tx Plan
              </span>
              <span
                className="text-[11px] font-medium px-2.5 py-0.5 rounded-full border"
                style={{
                  backgroundColor: getStatusBadge(
                    patient?.lastTreatmentPlanStatus,
                  ).bgColor,
                  color: getStatusBadge(patient?.lastTreatmentPlanStatus)
                    .mainColor,
                  borderColor: getStatusBadge(
                    patient?.lastTreatmentPlanStatus,
                  ).mainColor,
                }}
              >
                {formatPlanStatusLabel(
                  String(patient?.lastTreatmentPlanStatus),
                )}
              </span>
            </div>
          ) : null}
        </div>
      );
    })()}
    >
      <div className="flex min-h-[calc(100vh-4.5rem)] flex-1 flex-col overflow-y-auto p-0 min-[700px]:h-[calc(100vh-4.5rem)] min-[700px]:overflow-hidden">
        {/* <div className="flex shrink-0 items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-semibold">Patient Profile V2</h1>
            <p className="text-sm text-muted-foreground">
              Modular profile foundation for future patient experience.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={() => void refetch()} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div> */}

        <div className="flex w-full flex-1 flex-col min-[700px]:min-h-0 min-[700px]:flex-row min-[700px]:overflow-visible">
          <div
            className={cn(
              "relative flex w-full shrink-0 flex-col overflow-hidden border min-[700px]:overflow-visible min-[700px]:transition-all min-[700px]:duration-800 min-[700px]:ease-in-out",
              "min-h-[calc(100vh-4.5rem)] min-[700px]:min-h-0 min-[700px]:h-full",
              isLeftPanelOpen
                ? "min-[700px]:w-1/4 min-[700px]:min-w-[320px] min-[700px]:max-w-[360px]"
                : "min-[700px]:w-0 min-[700px]:min-w-0 min-[700px]:border-r-0",
            )}
          >
            <aside
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-hidden",
                !isLeftPanelOpen && "min-[700px]:invisible min-[700px]:pointer-events-none",
              )}
            >
              <ScrollArea className="min-h-0 flex-1">
                <div className="p-0 ">
                  <PatientSummaryMemo
                    key={`${patientId} + 'patientSummaryMemo' `}
                    patient={patient}
                    patientId={patientId}
                    carePlans={carePlans}
                    treatmentPlans={treatmentPlans}
                    devices={devices}
                    onPatientUpdate={setThePatient}
                    canAssignCaretaker={canAssignCaretaker}
                    canEditPatient={canEditPatient}
                    canAccessProfileActions={canAssignCaretaker}
                    canUpdatePatientPassword={canUpdatePatientPassword}
                    canManageSpecialInstructions={canManageSpecialInstructions}
                    isCaretaker={isCaretaker}
                    readingCount={readingCount}
                    readingCountLoading={readingCountLoading || readingCountPending}
                    selectedReadingMonth={selectedReadingMonth}
                    onSelectedReadingMonthChange={(month) => {
                      setReadingCountPending(month !== selectedReadingMonth);
                      setSelectedReadingMonth(month);
                    }}
                  />
                </div>
              </ScrollArea>
            </aside>
          </div>

          <main className="relative flex min-h-[calc(100vh-4.5rem)] min-w-0 flex-1 flex-col border-x border-b border-t-0 bg-[#F3F4F6] min-[700px]:min-h-0 min-[700px]:h-full min-[700px]:overflow-visible">
            <PatientProfilePanelToggle
              side="left"
              isOpen={isLeftPanelOpen}
              onToggle={toggleLeftPanel}
              className="hidden min-[700px]:flex"
            />
            <PatientProfilePanelToggle
              side="right"
              isOpen={isRightPanelOpen}
              onToggle={toggleRightPanel}
              className="hidden min-[700px]:flex"
            />
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <PatientTabsMemo
                key={`${patientId} + 'patientTabsMemo'`}
                patientId={patientId}
                patient={patient}
                carePlans={carePlans}
                treatmentPlans={treatmentPlans}
                devices={devices}
                nursingNotes={nursingNotes}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
                carePlanSubTab={carePlanSubTab}
                onCarePlanSubTabChange={setCarePlanSubTab}
                nursingNotesRefreshKey={nursingNotesRefreshKey}
                onOpenNursingNote={onOpenNursingNoteFromTab}
                onRefreshReadingCount={refreshReadingCount}
              />
            </div>
          </main>

          <div
            className={cn(
              "relative flex w-full shrink-0 flex-col overflow-hidden border bg-background min-[700px]:overflow-visible min-[700px]:transition-all min-[700px]:duration-800 min-[700px]:ease-in-out",
              "min-h-[calc(100vh-4.5rem)] min-[700px]:min-h-0 min-[700px]:h-full",
              isRightPanelOpen
                ? "min-[700px]:w-1/4 min-[700px]:min-w-[260px] min-[700px]:max-w-[360px]"
                : "min-[700px]:w-0 min-[700px]:min-w-0 min-[700px]:border-l-0",
            )}
          >
            <aside
              className={cn(
                "flex min-h-0 flex-1 flex-col overflow-hidden",
                !isRightPanelOpen && "min-[700px]:invisible min-[700px]:pointer-events-none",
              )}
            >
              <div className="shrink-0 overflow-auto border-b">
                <PatientRightPanelFieldsMemo
                  key={`${patientId} + 'patientRightPanelFieldsMemo'`}
                  patient={patient}
                  patientId={patientId}
                  carePlans={carePlans}
                  treatmentPlans={treatmentPlans}
                  canAssignCaretaker={canAssignCaretaker}
                  canManageSpecialInstructions={canManageSpecialInstructions}
                  onPatientUpdate={setThePatient}
                  onCarePlanClick={onCarePlanClick}
                  onTxPlanClick={onTxPlanClick}
                />
              </div>
              {canSeeInternalNotes ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <InternalNotesPanelMemo
                  key={`${patientId} + 'internalNotesPanelMemo'`}
                  patient={patient as Record<string, unknown> | null}
                  patientId={patientId}
                  notes={internalNotes}
                  setNotes={setInternalNotes}
                  authorName={authorName}
                  userId={String(
                      (user as { _id?: string; id?: string } | null)?._id ??
                      (user as { id?: string } | null)?.id ??
                      (user as { userId?: string } | null)?.userId ??
                      (user as { caretakerId?: string } | null)?.caretakerId ??
                      (profile as { _id?: string } | null)?._id ??
                      "",
                  )}
                  isAdmin={(user as { role?: string } | null)?.role === "admin"}
                  isPractitioner={(user as { role?: string } | null)?.role === "practitioner"}
                  isCaretaker={(user as { role?: string } | null)?.role === "caretaker"}
                  canAddOrEditInternalNotes={
                    !isProviderOrPractitioner && !isProviderOrPractitionerManager &&
                    (!isCaretaker || isCaretakerAssignedToPatient)
                  }
                  canAddNursingNote={canAddNursingNote}
                  onAddNursingNote={onAddNursingNote}
                />
              </div>
              ) : null}
            </aside>
          </div>
        </div>
      </div>
      <NursingNoteFormDialog
        open={nursingNoteDialogMode !== null}
        onOpenChange={(open) => {
          if (!open) {
            setNursingNoteDialogMode(null);
            setSelectedNursingNote(null);
          }
        }}
        patient={patient}
        patientId={patientId}
        mode={
          nursingNoteDialogMode === "view"
            ? "view"
            : nursingNoteDialogMode === "edit"
              ? "edit"
              : "add"
        }
        note={selectedNursingNote}
        authorName={authorName}
        onSaved={() => {
          setNursingNotesRefreshKey((key) => key + 1);
        }}
      />
    </DashboardLayout>
  );
}
